import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { useEffect, useState, Suspense, lazy } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { checkIfUsersExist } from "./Api/usersApi";
import { ADMIN_BASE } from "./config";

// ✅ Layouts & Core
import AdminLayout from "./Admin/Auth/AdminLayout";
import ProtectedRoute from "./Admin/Auth/ProtectedRoute";
import ClientLayout from "./Client/Auth/ClientLayout";
import PrivateRoute from "./Client/Auth/PrivateRoute";
import EventNotification from "./components/resident/EventNotifications";
import CookieConsent from "./Client/Pages/TrustLegal/CookieConsent";
import FloatingWidgets from "./Client/Pages/TrustLegal/FloatingWidgets";

import MyListings from "./Client/Pages/Community/MyListings";
import InitiativeDetail from "./Client/Pages/Community/InitiativeDetail";

// ✅ Admin Auth (lazy)
const Register = lazy(() => import("./Admin/Auth/Register"));
const Login = lazy(() => import("./Admin/Auth/Login"));
const ForgotPassword = lazy(() => import("./Admin/Auth/ForgotPassword"));
const ResetPassword = lazy(() => import("./Admin/Auth/ResetPassword"));

// ✅ Admin Pages (lazy)
const Dashboard = lazy(() => import("./Admin/Pages/Dashboard/Dashboard"));
const ContactInfo = lazy(() =>
  import("./Admin/Pages/Contact-Information/ContactInfo")
);
const UserGuide = lazy(() => import("./Admin/Pages/Dashboard/UserGuide"));
const Backup = lazy(() => import("./Admin/Pages/Dashboard/Backup"));
const ResidenceDetail = lazy(() =>
  import("./Admin/Pages/Residences-Management/ResidenceDetail")
);
const Residents = lazy(() =>
  import("./Admin/Pages/Residents-Management/Residents")
);
const NewsAnnouncements = lazy(() =>
  import("./Admin/Pages/Community-engagement/NewsAnnouncements")
);
const ServiceRequests = lazy(() =>
  import("./Admin/Pages/Requests-Management/ServiceRequests")
);
const ResidenceType = lazy(() =>
  import("./Admin/Pages/Residences-Management/ResidenceType")
);
const ResidenceSubType = lazy(() =>
  import("./Admin/Pages/Residences-Management/ResidenceSubType")
);
const MediaGallery = lazy(() =>
  import("./Admin/Pages/Community-engagement/MediaGallery")
);
const Update = lazy(() => import("./Admin/Pages/Community-engagement/Update"));
const SocialInitiative = lazy(() =>
  import("./Admin/Pages/Community-engagement/socialInitiative")
);

// ✅ Resident Auth Pages (lazy)
const ResidentForgotPassword = lazy(() =>
  import("./components/resident/ResidentForgotPassword")
);
const ResidentResetPassword = lazy(() =>
  import("./components/resident/ResidentResetPassword")
);
const Profile = lazy(() => import("./components/resident/Profile"));

// ✅ Client Pages (lazy)
const Home = lazy(() => import("./Client/Pages/Home/Home"));
const ContactUs = lazy(() => import("./Client/Pages/Contact Us/ContactUs"));
const AboutUs = lazy(() => import("./Client/Pages/About Us/AboutUs"));
const Community = lazy(() => import("./Client/Pages/Community/Community"));
const Residence = lazy(() => import("./Client/Pages/Community/Residence"));
const Amenity = lazy(() => import("./Client/Pages/Community/Amenity"));
const Details = lazy(() => import("./Client/Pages/Community/Details"));
const Service = lazy(() => import("./Client/Pages/Resident Service/Service"));
const NewsDetail = lazy(() => import("./Client/Pages/Home/NewsDetail"));
const GovernanceUpdate = lazy(() =>
  import("./Client/Pages/Community/GovernanceUpdate")
);
const ResdenceListing = lazy(() =>
  import("./Client/Pages/Community/ResidenceListing")
);
const Communication = lazy(() =>
  import("./Client/Pages/News Update/Communication")
);
const PrivacyPolicy = lazy(() =>
  import("./Client/Pages/TrustLegal/PrivacyPolicy")
);
const TermsAndConditions = lazy(() =>
  import("./Client/Pages/TrustLegal/TermsAndConditions")
);

// -------------------------------------------------------------------
// ✅ ScrollToTop Component
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// -------------------------------------------------------------------
function App() {
  const [loading, setLoading] = useState(true);
  const [firstUser, setFirstUser] = useState(null);
  const [theme, setTheme] = useState("light"); // ✅ dark mode state
  const location = useLocation();

  // ✅ Load theme from localStorage or system preference
  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme) {
      setTheme(savedTheme);
      document.documentElement.classList.toggle("dark", savedTheme === "dark");
    } else {
      const prefersDark = window.matchMedia(
        "(prefers-color-scheme: dark)"
      ).matches;
      setTheme(prefersDark ? "dark" : "light");
      document.documentElement.classList.toggle("dark", prefersDark);
    }
  }, []);

  // ✅ Watch theme changes and update <html> + localStorage
  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () =>
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));

  // ✅ Check if users exist
  useEffect(() => {
    const checkUsers = async () => {
      try {
        const res = await checkIfUsersExist();
        setFirstUser(res.data.firstUser);
      } catch (err) {
        console.error("Error checking users:", err);
        setFirstUser(false);
      } finally {
        setTimeout(() => setLoading(false), 1000);
      }
    };
    checkUsers();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    window.location.href = `${ADMIN_BASE}/login`;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gray-50 dark:bg-gray-900 z-50">
        <div className="flex flex-col items-center">
          <div className="w-20 h-20 rounded-full border-4 border-t-Dark border-b-Light animate-spin"></div>
          <p className="mt-4 text-gray-700 dark:text-gray-200 font-medium">
            Loading...
          </p>
        </div>
      </div>
    );
  }

  const isAdminRoute = location.pathname.startsWith(ADMIN_BASE);

  return (
    <>
      <Suspense
        fallback={
          <div className="fixed inset-0 flex items-center justify-center bg-gray-50 dark:bg-gray-900 z-50">
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 rounded-full border-4 border-t-Dark border-b-Light animate-spin"></div>
              <p className="mt-4 text-gray-700 dark:text-gray-200 font-medium">
                Loading page...
              </p>
            </div>
          </div>
        }
      >
        <Routes>
          {/* -------- Resident Public Routes -------- */}
          <Route path="/forgot-password" element={<ResidentForgotPassword />} />
          <Route
            path="/reset-password/:token"
            element={<ResidentResetPassword />}
          />

          {/* -------- Client Routes -------- */}
          <Route
            element={<ClientLayout theme={theme} toggleTheme={toggleTheme} />}
          >
            <Route path="/" element={<Home />} />
            <Route path="/contact-us" element={<ContactUs />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/news-detail" element={<NewsDetail />} />
            <Route path="/community" element={<Community />} />
            <Route path="/ccd-residences" element={<Residence />} />
            <Route path="/property-management" element={<ResdenceListing />} />
            <Route path="/ccd-details" element={<Details />} />
            <Route path="/initiative-details" element={<InitiativeDetail />} />
            <Route path="/ccd-amenity" element={<Amenity />} />
            <Route path="/service" element={<Service />} />
            <Route path="/governance-updates" element={<GovernanceUpdate />} />
            <Route path="/communications" element={<Communication />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-conditions" element={<TermsAndConditions />} />
            <Route
              path="/my-profile"
              element={
                <PrivateRoute>
                  <Profile />
                </PrivateRoute>
              }
            />
            <Route
              path="/my-listings"
              element={
                <PrivateRoute>
                  <MyListings />
                </PrivateRoute>
              }
            />
          </Route>

          {/* -------- Admin Auth & Public Routes -------- */}
          <Route
            path={`${ADMIN_BASE}/forgot-password`}
            element={<ForgotPassword />}
          />
          <Route
          
            path={`${ADMIN_BASE}/reset-password/:token`}
            element={<ResetPassword />}
          />
          {firstUser ? (
            <>
              <Route
                path={`${ADMIN_BASE}/register`}
                element={
                  <Register onRegisterSuccess={() => setFirstUser(false)} />
                }
              />
              <Route path={`${ADMIN_BASE}/login`} element={<Login />} />
              <Route
                path={`${ADMIN_BASE}/*`}
                element={<Navigate to={`${ADMIN_BASE}/register`} replace />}
              />
            </>
          ) : (
            <>
              <Route path={`${ADMIN_BASE}/login`} element={<Login />} />

              <Route path={ADMIN_BASE} element={<ProtectedRoute />}>
                <Route element={<AdminLayout onLogout={handleLogout} />}>
                  <Route index element={<Dashboard />} />
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="user-guide" element={<UserGuide />} />
                  <Route path="backup" element={<Backup />} />
                  <Route path="residences" element={<ResidenceDetail />} />
                  <Route path="residence-media" element={<MediaGallery />} />
                  <Route path="residence-type" element={<ResidenceType />} />
                  <Route
                    path="residence-subtype"
                    element={<ResidenceSubType />}
                  />
                  <Route path="residents" element={<Residents />} />
                  <Route path="requests" element={<ServiceRequests />} />
                  <Route path="news" element={<NewsAnnouncements />} />
                  <Route path="admin-updates" element={<Update />} />
                  <Route path="contact-info" element={<ContactInfo />} />
                  <Route
                    path="social-initiative"
                    element={<SocialInitiative />}
                  />
                </Route>

                <Route
                  path={`${ADMIN_BASE}/*`}
                  element={<Navigate to={`${ADMIN_BASE}/dashboard`} replace />}
                />
              </Route>
            </>
          )}

          {/* -------- Catch-all -------- */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>

      <ToastContainer
        position="bottom-right"
        theme="colored"
        autoClose={2000}
      />

      {/* ✅ Only on Resident Pages */}
      {!isAdminRoute && (
        <>
          <EventNotification />
          <CookieConsent />
          <FloatingWidgets />
        </>
      )}
    </>
  );
}

// -------------------------------------------------------------------
export default function AppWrapper() {
  return (
    <Router>
      <ScrollToTop />
      <App />
    </Router>
  );
}
